import { BuildingOffice2Icon } from '@heroicons/react/24/outline';
import React from 'react';

import { DiscriminatedProviderCreatedEvent } from '@/services/api/generated/matterChangesService';

import {
  ActivityActions,
  ActivityBody,
  ActivityIcon,
  ActivityItem,
  ActivityLink,
  ActivityTitle,
  CollapsibleList,
} from '../activity-feed-v2';
import { getUserFromEvent } from '../activity-feed-v2/ActivityDiffList';
import { formatEnumValue } from '../activity-feed-v2/utils';

export interface ProviderCreatedEventProps {
  event: DiscriminatedProviderCreatedEvent;
}

interface ProviderDetailField {
  label: string;
  value: string | number | null | undefined;
}

export const ProviderCreatedEvent: React.FC<ProviderCreatedEventProps> = ({
  event,
}) => {
  const { provider } = event.payload;
  const user = getUserFromEvent(event);

  const icon = (
    <ActivityIcon
      icon={
        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-teal-100">
          <BuildingOffice2Icon className="h-4 w-4 text-teal-600" />
        </div>
      }
    />
  );

  const providerDetailFields: ProviderDetailField[] = [
    {
      label: 'Specialty',
      value: provider?.provider_type
        ? formatEnumValue(provider.provider_type)
        : null,
    },
    { label: 'Address', value: provider?.address },
    { label: 'Description', value: provider?.description },
    { label: 'Phone', value: provider?.phone },
    { label: 'Fax', value: provider?.fax },
    { label: 'Email', value: provider?.email },
    { label: 'Still Treating', value: provider?.still_treating },
    {
      label: 'Treatment Status Date',
      value: provider?.treatment_status_occurred_at,
    },
    {
      label: 'Date of Records Request',
      value: provider?.date_of_records_request,
    },
    {
      label: 'Practitioners',
      value: provider?.practitioner_names?.length
        ? provider.practitioner_names.join(', ')
        : null,
    },
    { label: 'Pain Level', value: provider?.pain_level },
    { label: 'Treatment Notes', value: provider?.treatment_notes },
    {
      label: 'Treatment Frequency',
      value: provider?.treatment_frequency
        ? formatEnumValue(provider.treatment_frequency)
        : null,
    },
    {
      label: 'Treatment Progress',
      value: provider?.treatment_progress
        ? formatEnumValue(provider.treatment_progress)
        : null,
    },
  ];

  const detailItems = providerDetailFields
    .filter(
      ({ value }) => value !== null && value !== undefined && value !== '',
    )
    .map(({ label, value }) => `${label}: ${value}`);

  const body = (
    <ActivityBody>
      {provider?.name && <ActivityTitle>{provider.name}</ActivityTitle>}
      {detailItems.length > 0 && (
        <CollapsibleList
          items={detailItems}
          showLabel="Show Details"
          hideLabel="Hide Details"
        />
      )}
    </ActivityBody>
  );

  const actions = event.payload.target_id && (
    <ActivityActions>
      <ActivityLink
        href={`/matters/${event.matter_id}/treatment/${event.payload.target_id}`}
      >
        View Provider
      </ActivityLink>
    </ActivityActions>
  );

  return (
    <ActivityItem
      icon={icon}
      title="Provider Added"
      timestamp={new Date(event.occurred_at ?? new Date())}
      user={user}
      body={body}
      actions={actions}
    />
  );
};
